// AdminPanel/ApiKeysTab — admin-scoped view of one tenant's API keys.
//
// Distinct from DevelopersTool/ApiKeysTab (customer-facing — reads the
// caller's own keys via /api/v1/apikeys). This admin variant takes a
// tenantId and calls /api/v1/admin/tenants/{tenantId}/api_keys, so a
// support agent can inspect / generate / rotate any tenant's keys from
// the admin panel without impersonating.
//
// Backend RPCs (control_plane/onboarding_admin.proto):
//   - AdminListApiKeysByTenant     GET    /api/v1/admin/tenants/{tenant_id}/api_keys
//   - ProvisionMachineUserApiKey   POST   /api/v1/admin/tenants/{tenant_id}/api_keys/provision
//   - AdminRevokeApiKey            POST   /api/v1/admin/tenants/{tenant_id}/api_keys/{id}/revoke
//
// Rotation model = OVERLAP, not in-place. MaxKeysPerProject (default 2) is
// enforced server-side, so the operator can "Generate" a 2nd key, move the
// integration to it (watch the "Last used" column flip), then "Revoke" the
// old one — zero downtime. Any non-revoked PAT authenticates equally (auth
// validates via Zitadel introspection), so there is no "primary" key.
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Add, ContentCopy, VpnKey, Block} from '@mui/icons-material';
import {
  Box, Button, Card, CardContent, Typography, Chip, CircularProgress, IconButton,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Alert, Snackbar,
} from '@mui/material';

import {
  adminListApiKeysByTenant,
  adminProvisionMachineUserApiKey,
  adminRevokeApiKey,
} from '../../actions/adminPanel';

// Grid columns. "Last used" is only shown when at least one key actually
// has a last_used_at (fresh tenants whose keys were never used would just
// show a column of "never" — hide it until there's signal worth showing).
const GRID_WITH_LASTUSED = '1.1fr 0.9fr 100px 150px 140px 110px'; // 6 cols
const GRID_NO_LASTUSED   = '1.2fr 1fr 120px 150px 120px';          // 5 cols (no Last used)

// Relative "time ago" for the Last-used column — the signal that tells an
// operator which key is actually receiving traffic, so they revoke the
// idle one during a zero-downtime rotation (never seen = safe to retire).
function timeAgo(ts) {
  if (!ts) return 'never';
  const then = new Date(ts).getTime();
  if (Number.isNaN(then)) return '—';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// Only SMS is exposed to the support panel right now. The backend RPC
// (ProvisionMachineUserApiKey) accepts whatsapp/email/rcs too, but those
// channels either have their own provisioning paths (WhatsApp's WA
// orchestrator) or aren't live yet. SMS is the only live channel, so the
// Channel column and the dialog's channel picker are hidden
// (createForm.project stays 'sms'); this list still drives the per-project
// cap that disables "Generate API Key". If a second channel flips to this
// path, re-add the picker.
const PROJECT_CHOICES = ['sms'];

// Maximum keys allowed per (tenant, project) on the backend
// (APIKEY_MAX_KEYS_PER_PROJECT env, default=2). Mirrors the cap so the
// UI can disable "Add" once the limit is hit instead of waiting for a
// server-side rejection. Keep in sync with apikey.config.go.
const MAX_KEYS_PER_PROJECT = 2;

class AdminApiKeysTab extends Component {
  state = {
    keys: [],
    loading: false,
    createDialogOpen: false,
    createForm: {
      project: 'sms',
      keyName: 'default',
      description: '',
    },
    creating: false,
    createdKey: null,         // { id, plaintext_token, key_prefix, reused }
    revokeConfirmId: null,    // key id awaiting Confirm/Cancel
    revokingId: null,         // key id currently being revoked
    snackbar: {open: false, message: '', severity: 'success'},
  };

  componentDidMount() {
    this.fetchKeys();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.tenantId !== this.props.tenantId) {
      this.fetchKeys();
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({createdKey: null, creating: false});
    }
    // React to provisioning response landing. handleAPIDispatch is a
    // fire-and-forget thunk (doesn't return a Promise), so we watch the
    // store-backed prop for a reference change while a creating flag is
    // set. The flag prevents stale responses from a prior open from
    // re-triggering the success/error path on remount.
    if (
      this.state.creating &&
      prevProps.adminProvisionApiKeyResponse !== this.props.adminProvisionApiKeyResponse
    ) {
      this.handleProvisionSettled();
    }
  }

  fetchKeys = () => {
    const {tenantId} = this.props;
    if (!tenantId) return;
    this.setState({loading: true});
    this.props.adminListApiKeysByTenant(tenantId);
  };

  // Pull list out of the Redux response — handleAPIDispatch persists the
  // raw axios response payload at adminApiKeysResponse, so we mirror the
  // existing reducer access shape used by other admin tabs.
  static getDerivedStateFromProps(nextProps, prevState) {
    const resp = nextProps.adminApiKeysResponse;
    if (!resp) return null;
    // Avoid clobbering optimistic local state when the response hasn't
    // changed reference between renders.
    if (resp === prevState._lastListResp) return null;
    const keys = resp?.data?.keys || resp?.keys || [];
    return {
      keys: Array.isArray(keys) ? keys : [],
      loading: false,
      _lastListResp: resp,
    };
  }

  openCreateDialog = () => {
    this.setState({
      createDialogOpen: true,
      createForm: {project: 'sms', keyName: 'default', description: ''},
      createdKey: null,
    });
  };

  closeCreateDialog = () => {
    this.setState({createDialogOpen: false});
  };

  updateForm = (field, value) => {
    this.setState((s) => ({createForm: {...s.createForm, [field]: value}}));
  };

  // Counts active keys for a given project. The cap is enforced over
  // (tenant, project) on the backend; the UI mirrors that so the "Add"
  // button disables when both slots are taken (without replace_existing).
  activeCountForProject(project) {
    return this.state.keys.filter(
      (k) => (k.isActive ?? k.is_active) && k.project === project
    ).length;
  }

  submitCreate = () => {
    const {tenantId} = this.props;
    const {project, keyName, description} = this.state.createForm;
    if (!project) return;

    this.setState({creating: true});
    const opts = {
      keyName: keyName?.trim() || 'default',
      description: description?.trim() || undefined,
    };
    // handleAPIDispatch is a fire-and-forget thunk — the response lands
    // in adminProvisionApiKeyResponse via the reducer, and we react to it
    // in componentDidUpdate. No return-value chaining.
    this.props.adminProvisionMachineUserApiKey(tenantId, project, opts);
  };

  handleProvisionSettled = () => {
    const resp = this.props.adminProvisionApiKeyResponse;
    if (!resp || resp.success === false) {
      const msg = resp?.error || 'Failed to provision API key';
      this.setState({
        creating: false,
        snackbar: {open: true, message: msg, severity: 'error'},
      });
      return;
    }
    const createdKey = {
      api_key_id: resp.apiKeyId ?? resp.api_key_id,
      plaintext_token: resp.plaintextToken ?? resp.plaintext_token,
      key_prefix: resp.keyPrefix ?? resp.key_prefix,
      zitadel_user_id: resp.zitadelUserId ?? resp.zitadel_user_id,
      reused: !!resp.reused,
    };
    this.setState({
      creating: false,
      createDialogOpen: false,
      createdKey,
      snackbar: {
        open: true,
        message: createdKey.reused ? 'Existing API key returned' : 'API key created',
        severity: 'success',
      },
    });
    this.fetchKeys();
  };

  copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      this.setState({snackbar: {open: true, message: 'Copied to clipboard', severity: 'success'}});
    }).catch(() => {
      // Fallback for older browsers / non-secure contexts.
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (_) { /* noop */ }
      document.body.removeChild(ta);
    });
  };

  // Revoke a single key (overlap rotation: generate a 2nd key, migrate the
  // integration, then revoke the older one here — zero downtime). Two-step
  // confirm so a misclick doesn't kill a live key. Refetches after.
  askRevoke = (id) => this.setState({revokeConfirmId: id});
  cancelRevoke = () => this.setState({revokeConfirmId: null});
  confirmRevoke = (id) => {
    const {tenantId} = this.props;
    this.setState({revokingId: id, revokeConfirmId: null});
    this.props.adminRevokeApiKey(tenantId, id);
    // Fire-and-forget thunk → reflect the new status on the next list read.
    setTimeout(() => {
      this.setState({revokingId: null,
        snackbar: {open: true, message: 'API key revoked', severity: 'success'}});
      this.fetchKeys();
    }, 600);
  };

  renderKeyRow(k, showLastUsed) {
    const keyName = k.keyName ?? k.key_name;
    const keyPrefix = k.keyPrefix ?? k.key_prefix;
    const createdAt = k.createdAt ?? k.created_at;
    const lastUsedAt = k.lastUsedAt ?? k.last_used_at;
    const lastUsedIp = k.lastUsedIp ?? k.last_used_ip;
    const isActive = k.isActive ?? k.is_active;
    const {revokeConfirmId, revokingId} = this.state;
    return (
      <Box key={k.id} sx={{
        display: 'grid',
        gridTemplateColumns: showLastUsed ? GRID_WITH_LASTUSED : GRID_NO_LASTUSED,
        gap: 1, alignItems: 'center',
        py: 1, borderBottom: '1px solid #eee',
      }}>
        <Box>
          <Typography variant="body2" component="div" sx={{fontWeight: 500}}>
            {keyName || '-'}
          </Typography>
          {k.description && (
            <Typography variant="caption" color="text.secondary" component="div">
              {k.description}
            </Typography>
          )}
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, fontFamily: 'monospace', fontSize: 12}}>
          <code style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220}}>
            {keyPrefix || '-'}
          </code>
          {k.token && (
            <Tooltip title="Copy full token">
              <IconButton size="small" onClick={() => this.copyToClipboard(k.token)}>
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {isActive
          ? <Chip label="Active" size="small" color="success" />
          : <Chip label="Revoked" size="small" color="error" variant="outlined" />}
        {showLastUsed && (
          <Tooltip title={lastUsedAt
            ? new Date(lastUsedAt).toLocaleString() + (lastUsedIp ? ` · from ${lastUsedIp}` : '')
            : 'Never used — safe to revoke'}>
            <Typography variant="caption" color={lastUsedAt ? 'text.primary' : 'text.secondary'}>
              {timeAgo(lastUsedAt)}
            </Typography>
          </Tooltip>
        )}
        <Typography variant="caption" color="text.secondary">
          {createdAt ? new Date(createdAt).toLocaleString() : '-'}
        </Typography>
        <Box>
          {isActive && (
            revokeConfirmId === k.id ? (
              <>
                <Button size="small" color="error" disabled={revokingId === k.id}
                  onClick={() => this.confirmRevoke(k.id)}>Confirm</Button>
                <Button size="small" onClick={this.cancelRevoke} disabled={revokingId === k.id}>Cancel</Button>
              </>
            ) : (
              <Tooltip title="Revoke this key (use after migrating to a newer key — zero downtime)">
                <span>
                  <Button size="small" color="error" startIcon={<Block />}
                    disabled={revokingId === k.id} onClick={() => this.askRevoke(k.id)}>
                    Revoke
                  </Button>
                </span>
              </Tooltip>
            )
          )}
          {revokingId === k.id && <CircularProgress size={16} style={{marginLeft: 8, verticalAlign: 'middle'}} />}
        </Box>
      </Box>
    );
  }

  render() {
    const {tenantId} = this.props;
    const {keys, loading, createDialogOpen, createForm, creating, createdKey, snackbar} = this.state;

    const activeKeys = keys.filter((k) => k.isActive ?? k.is_active);
    const allSlotsFull = PROJECT_CHOICES.every(
      (p) => this.activeCountForProject(p) >= MAX_KEYS_PER_PROJECT
    );
    // Only surface the Last-used column once at least one key has actually
    // been used — otherwise it's a column of "never" that adds no signal.
    const showLastUsed = keys.some((k) => k.lastUsedAt ?? k.last_used_at);

    return (
      <Box>
        <Card sx={{mb: 2}}>
          <CardContent>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2}}>
              <Box sx={{minWidth: 0}}>
                <Typography variant="h6">API Keys</Typography>
                <Typography variant="caption" color="text.secondary">
                  Machine-user PATs scoped to this tenant. Up to {MAX_KEYS_PER_PROJECT} keys per channel — generate a second key for zero-downtime rotation, then revoke the older one.
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{mt: 0.5}}>
                  Tenant: <code>{tenantId || '-'}</code> &nbsp;·&nbsp; {activeKeys.length} active key{activeKeys.length === 1 ? '' : 's'}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={this.openCreateDialog}
                disabled={!tenantId || allSlotsFull}
                sx={{whiteSpace: 'nowrap', flexShrink: 0}}
              >
                Generate API Key
              </Button>
            </Box>
          </CardContent>
        </Card>

        {createdKey && (
          <Alert
            severity="success"
            onClose={() => this.setState({createdKey: null})}
            sx={{mb: 2}}
            action={
              <Tooltip title="Copy token">
                <IconButton size="small" onClick={() => this.copyToClipboard(createdKey.plaintext_token)}>
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          >
            <strong>{createdKey.reused ? 'Existing key:' : 'Save this key now — Zitadel will not show it again:'}</strong>
            <br />
            <code style={{fontSize: 12, wordBreak: 'break-all'}}>{createdKey.plaintext_token}</code>
          </Alert>
        )}

        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}><CircularProgress /></Box>
            ) : keys.length === 0 ? (
              <Box sx={{textAlign: 'center', py: 4, color: 'text.secondary'}}>
                <VpnKey sx={{fontSize: 48, color: '#ccc'}} />
                <Typography variant="body2" sx={{mt: 1}}>
                  No API keys for this tenant yet. Click <strong>Generate API Key</strong> to provision the first one.
                </Typography>
              </Box>
            ) : (
              <Box>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: showLastUsed ? GRID_WITH_LASTUSED : GRID_NO_LASTUSED,
                  gap: 1, py: 1, borderBottom: '2px solid #ddd',
                  fontWeight: 600, fontSize: 13, color: 'text.secondary',
                }}>
                  <Box>Name</Box>
                  <Box>Prefix / Token</Box>
                  <Box>Status</Box>
                  {showLastUsed && <Box>Last used</Box>}
                  <Box>Created</Box>
                  <Box>Actions</Box>
                </Box>
                {keys.map((k) => this.renderKeyRow(k, showLastUsed))}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Generate (additive) dialog — rotation is generate-new + revoke-old */}
        <Dialog open={createDialogOpen} onClose={this.closeCreateDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Generate API Key</DialogTitle>
          <DialogContent>
            <TextField
              label="Key Name"
              fullWidth
              variant="outlined"
              value={createForm.keyName}
              onChange={(e) => this.updateForm('keyName', e.target.value)}
              sx={{mb: 2}}
              helperText="Lowercase identifier shown in lists (default: 'default')."
            />
            <TextField
              label="Description (optional)"
              fullWidth
              variant="outlined"
              multiline
              rows={2}
              value={createForm.description}
              onChange={(e) => this.updateForm('description', e.target.value)}
              sx={{mb: 2}}
            />
            <Typography variant="caption" color="text.secondary">
              Adds a key (up to {MAX_KEYS_PER_PROJECT} per channel). To rotate with zero downtime:
              generate a second key, move your integration to it, then <strong>Revoke</strong> the old one.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeCreateDialog} disabled={creating}>Cancel</Button>
            <Button
              variant="contained"
              onClick={this.submitCreate}
              disabled={creating || !createForm.project}
              color="primary"
            >
              {creating ? <CircularProgress size={20} /> : 'Generate'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => this.setState({snackbar: {...snackbar, open: false}})}
          anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        >
          <Alert severity={snackbar.severity} onClose={() => this.setState({snackbar: {...snackbar, open: false}})}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }
}

AdminApiKeysTab.propTypes = {
  tenantId: PropTypes.string,
  adminApiKeysResponse: PropTypes.object,
  adminProvisionApiKeyResponse: PropTypes.object,
  adminListApiKeysByTenant: PropTypes.func.isRequired,
  adminProvisionMachineUserApiKey: PropTypes.func.isRequired,
  adminRevokeApiKey: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    adminApiKeysResponse: state.adminPanel.adminApiKeysResponse,
    adminProvisionApiKeyResponse: state.adminPanel.adminProvisionApiKeyResponse,
  };
}

export default connect(mapStateToProps, {
  adminListApiKeysByTenant,
  adminProvisionMachineUserApiKey,
  adminRevokeApiKey,
})(AdminApiKeysTab);
